import { useMemo } from 'react';
import { useEditorState } from '../contexts';

type DiffLine = {
  type: 'add' | 'del' | 'equal';
  text: string;
  left?: number;
  right?: number;
};

type UseDiffViewParams = {
  persistedFiles: Record<string, string>;
  workingFiles: Record<string, string>;
};

export function useDiffView({
  persistedFiles,
  workingFiles,
}: UseDiffViewParams) {
  // Get editor state from context
  const { activeFile } = useEditorState();
  const diffLines = useMemo<DiffLine[]>(() => {
    if (!activeFile) return [];
    const original = (persistedFiles[activeFile] ?? '').split('\n');
    const current = (workingFiles[activeFile] ?? original.join('\n')).split('\n');
    const maxLen = Math.max(original.length, current.length);
    const lines: DiffLine[] = [];
    for (let i = 0; i < maxLen; i += 1) {
      const left = original[i];
      const right = current[i];
      const leftNo = left !== undefined ? i + 1 : undefined;
      const rightNo = right !== undefined ? i + 1 : undefined;
      if (left === right) {
        if (left !== undefined) lines.push({ type: 'equal', text: `  ${left}`, left: leftNo, right: rightNo });
        continue;
      }
      if (left !== undefined && right !== undefined) {
        lines.push({ type: 'del', text: `- ${left}`, left: leftNo });
        lines.push({ type: 'add', text: `+ ${right}`, right: rightNo });
      } else if (left !== undefined) {
        lines.push({ type: 'del', text: `- ${left}`, left: leftNo });
      } else if (right !== undefined) {
        lines.push({ type: 'add', text: `+ ${right}`, right: rightNo });
      }
    }
    return lines.slice(0, 400);
  }, [activeFile, persistedFiles, workingFiles]);

  return { diffLines };
}
